import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { SESSION_ENDED } from '../../consts/consts';
import type { ReduxState } from '../../types';
import { navigationService } from '../../utils/api/navigationService';
import {
  getLastKnownMeId,
  subscribeToAuthChanged,
} from '../../utils/api/sessionBroadcast';

const LOOP_GUARD_KEY = 'confirm.sessionSync.justReloaded';
const LOOP_GUARD_WINDOW_MS = 10_000;

const reloadOnce = () => {
  try {
    const previous = sessionStorage.getItem(LOOP_GUARD_KEY);
    const now = Date.now();
    if (previous && now - Number(previous) < LOOP_GUARD_WINDOW_MS) {
      // Already reloaded recently; bail out to avoid a reload loop.
      sessionStorage.removeItem(LOOP_GUARD_KEY);
      return;
    }
    sessionStorage.setItem(LOOP_GUARD_KEY, String(now));
  } catch {
    // sessionStorage unavailable; proceed without guard.
  }
  navigationService.reload();
};

export const SessionSyncWatcher = () => {
  const reduxMeId = useSelector<ReduxState, number | null>(
    (state) => state.me?.id ?? null
  );
  const isProxying = useSelector<ReduxState, boolean>((state) =>
    Boolean(state.currentProxyPerson)
  );

  useEffect(() => {
    const checkSession = () => {
      // Tab not yet logged in; nothing to compare.
      if (reduxMeId == null) return;

      if (isProxying) return;

      // Don't bounce ourselves while we're already on the session-ended
      // screen — the user is on a stable page and may not have clicked
      // sign-in yet.
      if (navigationService.pathname === SESSION_ENDED.path) return;

      const stored = getLastKnownMeId();

      if (stored == null) {
        // Another tab logged out and cleared the key. Send this tab to
        // the standalone "session ended" page so the user gets a clear
        // explanation rather than being silently dumped at login.
        // Using assign (not the /logout route) avoids re-running
        // logOutAndCleanUp here, which would re-broadcast and create a
        // reload cascade across tabs.
        navigationService.assign(SESSION_ENDED.path);
        return;
      }

      if (String(reduxMeId) !== stored) {
        // Another tab logged in as a different user. A reload picks up
        // the new auth state cleanly.
        reloadOnce();
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') checkSession();
    };

    const unsubscribe = subscribeToAuthChanged(checkSession);
    window.addEventListener('focus', checkSession);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      unsubscribe();
      window.removeEventListener('focus', checkSession);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [reduxMeId, isProxying]);

  return null;
};

export default SessionSyncWatcher;
